import { QueryKeys, ThreeMinutes } from "@Constants";
import SessionManager from "@Core/session";
import {
    IActivityCategoryDefinition,
    IActivityDefinition,
    IActivityDefinitions,
    IActivityDetails,
} from "@Models/IActivityDetails";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { IRolePayload } from "@Models/IRoleDetails";
import { IUserGroupDetails } from "@Models/IUserGroupDetails";
import ActivityService from "@Services/ActivityService";
import FeedbackService from "@Services/FeedbackService";
import UserGroupService from "@Services/UserGroupService";
import PromptService from "@Services/PromptService";
import UserService from "@Services/UserService";
import { arrayToMapByKey, isNil, uniqueArrayOfObjectsByKey } from "@Utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import appQueryClient from "@AppQueryClient";
import { IProductDetails } from "@Models/IProductDetails";
import TermsService from "@Services/TermsService";
import FeatureFlagService from "@Services/FeatureFlagService";

export const useSubmissionsQuery = (activityInstanceIds: IActivityDetails["activityInstanceId"][]) => {
    const activityService = new ActivityService();
    return useQuery<IActivityDetails[]>(
        [QueryKeys.ActivitySubmissions, activityInstanceIds],
        async () => {
            return await activityService
                .getActivities(activityInstanceIds)
                .then((res) => (!res.errors.length ? res.value : []));
        },
        {
            enabled: !!activityInstanceIds.length,
            placeholderData: [],
            refetchOnWindowFocus: false,
            staleTime: ThreeMinutes,
        }
    );
};

export const useConnectionsQuery = (userId = SessionManager.getServiceUser()?.userId) => {
    const service = new UserService();
    return useQuery(
        [QueryKeys.UserConnections], // no need to added userId to key because it's only the active user's connections
        async () => {
            const result = await service.getUserConnections(userId);
            return !result.hasErrors ? result.value : [];
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
            enabled: !!SessionManager.getServiceUser(),
        }
    );
};

/**
 *
 * This returns groups for the current user or a user requested
 */
export const useGroupsQuery = (userId = SessionManager.getServiceUser()?.userId) => {
    const userGroupService = new UserGroupService();
    return useQuery<IUserGroupDetails[]>(
        [QueryKeys.UserGroups, userId],
        async () => {
            if (!userId) return [];
            return await userGroupService.getUser(userId).then((res) => (!res.errors.length ? res?.value || [] : []));
        },
        {
            enabled: !!userId,
            placeholderData: [],
            // sometimes there are duplicates in the array
            select: (userGroups: IUserGroupDetails[]) => uniqueArrayOfObjectsByKey(userGroups, "groupId"),
        }
    );
};

export const useTemplatesQuery = (groupIds?: string[]) => {
    const feedbackService = new FeedbackService();

    // this hook does not actually fetch unless groupIds is present;
    const { data: groups } = useGroupsQuery(groupIds ? "" : undefined);
    const ids = !!groupIds ? groupIds : groups.map((group) => group.groupId);

    return useQuery<IFeedbackTemplateDetails[]>(
        [QueryKeys.Templates],
        async () => {
            return await feedbackService.getTemplates(ids).then((res) => {
                if (!res.errors.length) {
                    return res.value;
                }
                return [];
            });
        },
        {
            enabled: ids?.length > 0,
            placeholderData: [],
            refetchOnWindowFocus: false,
        }
    );
};

export const useUserAccountDetailsQuery = (userId = SessionManager.getServiceUser()?.userId) => {
    const service = new UserService();
    return useQuery(
        [QueryKeys.UserAccountDetails, userId],
        async () => {
            const user = await service.getPublic([userId]);
            return !user.hasErrors ? user.value : [];
        },
        {
            enabled: !!SessionManager.getServiceUser(),
            refetchOnWindowFocus: false,
            select: (data) => data.find((x) => x.userId === userId),
        }
    );
};

export const useCapabilityIdsByCapabilityName = (capabilities: string[]) => {
    const rolesQuery = appQueryClient.getQueryData<IRolePayload>([QueryKeys.Roles]);
    const capabilityIdsByCapabilityName = useMemo(
        () => arrayToMapByKey(rolesQuery?.capabilities, (item) => item.name, true, "capabilityId"),
        [rolesQuery?.capabilities]
    );
    if (!rolesQuery.capabilities?.length) return [];
    return capabilities?.map((cap) => capabilityIdsByCapabilityName.get(cap));
};

export const useDefinitionsQuery = () => {
    // TODO: REVIEW appQueryClient.getQueryData<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);
    return useQuery<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);
};

export const useDefinitionsActivitiesAndCategoriesQuery = () => {
    interface IDefinitionsActivitiesAndCategories {
        activitiesMap: Map<string, IActivityDefinition>;
        activitiesMapByCategory: Map<string, IActivityDefinition[]>;
        activityDefinitions: IActivityDefinitions;
        categoriesMap: Map<string, IActivityCategoryDefinition>;
        categoryIds: Set<string>;
        isFetched: boolean;
        parentCategoryIds: Set<string>;
        productIds: Set<string>;
        productsMap: Map<string, IProductDetails>;
    }

    const PLACEHOLDER_DATA: IDefinitionsActivitiesAndCategories = {
        activitiesMap: new Map(),
        activitiesMapByCategory: new Map(),
        activityDefinitions: { products: [], activities: [], categories: [] },
        categoriesMap: new Map(),
        categoryIds: new Set(),
        isFetched: false,
        parentCategoryIds: new Set(),
        productIds: new Set(),
        productsMap: new Map(),
    };

    const { data: definitions, isFetched, ...otherProps } = useDefinitionsQuery();

    if (!isFetched || isNil(definitions)) return { ...PLACEHOLDER_DATA, isFetched, ...otherProps };

    const productsMap = arrayToMapByKey(definitions?.products, (a) => a.productId, true);
    const activitiesMap = arrayToMapByKey(definitions?.activities, (a) => a.activityId, true);
    const categoriesMap = arrayToMapByKey(definitions?.categories, (c) => c.activityCategoryId, true);

    const activitiesMapByCategory = arrayToMapByKey(definitions?.activities, (a) => a.activityCategoryId);
    const productIds = new Set<string>(definitions?.products.map((c) => c.productId));
    const categoryIds = new Set<string>(definitions?.categories.map((c) => c.activityCategoryId));
    const parentCategoryIdsSet = new Set<string>(definitions?.categories.map((c) => c.activityCategoryId));
    const parentCategoryIds = new Set<string>(
        definitions?.categories
            .filter((c) => !parentCategoryIdsSet.has(c.parentCategoryId))
            .map((c) => c.activityCategoryId)
    );

    const data = {
        ...otherProps,
        activitiesMap,
        activitiesMapByCategory,
        activityDefinitions: definitions,
        categoriesMap,
        categoryIds,
        isFetched,
        parentCategoryIds,
        productIds,
        productsMap,
    };

    return data;
};

export const useGetUserPrompts = () => {
    const fetchUserPrompts = async () => {
        const service = new PromptService();
        const fetchedPrompts = await service.getUserPrompts();
        if (fetchedPrompts.hasErrors) {
            throw new Error(`Failed to fetch user prompts.`);
        }
        return fetchedPrompts.value;
    };

    const { data, isLoading, isFetching, error } = useQuery([QueryKeys.UserPrompts], fetchUserPrompts, {
        staleTime: ThreeMinutes,
        refetchOnWindowFocus: true,
    });

    return { data, isLoading, isFetching, error };
};

/**
 * Hook to fetch the latest terms
 */
export const useGetLatestTerms = (type: string) => {
    const fetchTerms = async () => {
        const service = new TermsService();
        const fetchedTerms = await service.getLatestTerms(type);
        if (fetchedTerms.hasErrors) {
            throw new Error(`Failed to fetch terms for type: ${type}.`);
        }
        return fetchedTerms.value;
    };

    const { data, isLoading, error } = useQuery([QueryKeys.Terms, type], fetchTerms, {
        enabled: !!type, // Only fetch if type is provided
        staleTime: ThreeMinutes,
        refetchOnWindowFocus: false,
    });

    return { data, isLoading, error };
};

/**
 * Hook to fetch all terms
 */
export const useGetAllTerms = () => {
    const fetchTerms = async () => {
        const service = new TermsService();
        const fetchedTerms = await service.getAllTerms();
        if (fetchedTerms.hasErrors) {
            throw new Error(`Failed to fetch terms.`);
        }
        return fetchedTerms.value;
    };

    const { data, isLoading, error } = useQuery([QueryKeys.Terms], fetchTerms, {
        staleTime: ThreeMinutes,
        refetchOnWindowFocus: false,
    });

    return { data, isLoading, error };
};

/**
 * Hook to fetch user accepted terms
 */
export const useGetUserAcceptedTerms = (type: string, latest: boolean) => {
    const fetchUserAcceptedTerms = async () => {
        const service = new TermsService();
        const fetchedTerms = await service.getUserAcceptedTerms(type, latest);
        if (fetchedTerms.hasErrors) {
            throw new Error(`Failed to fetch user accepted terms for type: ${type}.`);
        }
        return fetchedTerms.value;
    };

    const { data, isLoading, error } = useQuery([QueryKeys.UserAcceptedTerms, type, latest], fetchUserAcceptedTerms, {
        staleTime: ThreeMinutes,
        refetchOnWindowFocus: false,
    });

    return { data, isLoading, error };
};

export const useHasFeature = (featureFlag: string) => {
    const fetchFeatureFlag = async () => {
        const service = new FeatureFlagService();
        const fetchedFeatureFlag = await service.getFeatureFlag(featureFlag);
        if (fetchedFeatureFlag.hasErrors) {
            throw new Error(`Failed to fetch feature flag for featureFlag: ${featureFlag}.`);
        }
        return fetchedFeatureFlag.value?.isActive;
    };

    const { data, isLoading, error } = useQuery([QueryKeys.FeatureFlag, featureFlag], fetchFeatureFlag, {
        staleTime: ThreeMinutes,
        refetchOnWindowFocus: false,
    });

    return { data, isLoading, error };
};
